import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import {
  getUserAccessStatus,
  updateUserAccessStatus,
} from "../api/userAccessApi";

const ROLE_LABELS = {
  OWNER: "Dueño",
  CONSULTORA: "Consultora",
  MANAGER: "Administrador",
  EMPLOYEE: "Empleado",
};

const EMPLOYEE_STATUS_LABELS = {
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
};

function normalizeBoolean(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true"
  );
}

function normalizeUser(row) {
  return {
    userId: Number(row.userId ?? row.id),
    displayName: row.displayName ?? row.employeeFullName ?? "—",
    email: row.email ?? "—",
    userRole: row.userRole ?? row.user_role ?? "—",
    isActive: normalizeBoolean(row.isActive ?? row.is_active),
    employeeId: row.employeeId ? Number(row.employeeId) : null,
    employeeStatus: row.employeeStatus ?? null,
  };
}

function getRoleLabel(role) {
  return ROLE_LABELS[role] ?? role ?? "—";
}

function getRoleChipColor(role) {
  if (role === "OWNER") return "warning";
  if (role === "MANAGER") return "primary";
  if (role === "EMPLOYEE") return "secondary";
  return "default";
}

export default function UserAccessConfigPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [pendingChange, setPendingChange] = useState(null);

  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getUserAccessStatus();
      setUsers((response || []).map(normalizeUser));
    } catch (error) {
      console.error("user access status", error);
      setUsers([]);
      setToast({
        open: true,
        severity: "error",
        message: error?.message || "No se pudieron cargar los usuarios.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return users;

    return users.filter((user) => {
      const haystack = [
        user.displayName,
        user.email,
        user.userRole,
        getRoleLabel(user.userRole),
        user.employeeStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [users, search]);

  const activeCount = useMemo(
    () => users.filter((user) => user.isActive).length,
    [users]
  );

  const inactiveCount = useMemo(
    () => users.filter((user) => !user.isActive).length,
    [users]
  );

  const openConfirm = (user, nextIsActive) => {
    setPendingChange({
      user,
      nextIsActive,
    });
  };

  const closeConfirm = () => {
    if (savingUserId) return;
    setPendingChange(null);
  };

  const confirmChange = async () => {
    if (!pendingChange?.user) return;

    const { user, nextIsActive } = pendingChange;

    setSavingUserId(user.userId);

    try {
      await updateUserAccessStatus(user.userId, nextIsActive);

      setUsers((prev) =>
        prev.map((item) =>
          Number(item.userId) === Number(user.userId)
            ? { ...item, isActive: nextIsActive }
            : item
        )
      );

      setToast({
        open: true,
        severity: "success",
        message: nextIsActive
          ? "Acceso activado correctamente."
          : "Acceso desactivado correctamente.",
      });

      setPendingChange(null);
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message:
          error?.message ||
          (nextIsActive
            ? "No se pudo activar el acceso."
            : "No se pudo desactivar el acceso."),
      });
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "24px",
          bgcolor: "neutral.200",
          boxShadow: "inset 0px 0px 6px rgba(64,64,94,0.25)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ManageAccountsIcon sx={{ color: "primary.900" }} />
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 900,
                  color: "primary.900",
                }}
              >
                Acceso de usuarios
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "neutral.700",
                mt: 0.5,
              }}
            >
              Administrá qué usuarios pueden ingresar al sistema.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loading || Boolean(savingUserId)}
            sx={{
              alignSelf: { xs: "flex-start", md: "center" },
              borderRadius: "33px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Actualizar lista
          </Button>
        </Stack>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Paper
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: "24px",
              bgcolor: "neutral.50",
              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, email o rol..."
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: { md: 420 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "999px",
                    bgcolor: "rgba(241,243,249,0.95)",
                    fontSize: "14px",
                    fontWeight: 600,
                    "& fieldset": {
                      borderColor: "transparent",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(40, 80, 160, 0.35)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(14, 50, 149, 0.85)",
                    },
                  },
                }}
              />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  label={`${users.length} usuarios`}
                  size="small"
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  label={`${activeCount} activos`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  label={`${inactiveCount} sin acceso`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 800 }}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper
            sx={{
              borderRadius: "24px",
              bgcolor: "neutral.50",
              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
            }}
          >
            <TableContainer sx={{ maxHeight: "58vh" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: "rgba(166,166,178,0.95)",
                        color: "primary.900",
                        fontWeight: 900,
                        fontSize: 13,
                      }}
                    >
                      Usuario
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "rgba(166,166,178,0.95)",
                        color: "primary.900",
                        fontWeight: 900,
                        fontSize: 13,
                      }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "rgba(166,166,178,0.95)",
                        color: "primary.900",
                        fontWeight: 900,
                        fontSize: 13,
                      }}
                    >
                      Rol
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "rgba(166,166,178,0.95)",
                        color: "primary.900",
                        fontWeight: 900,
                        fontSize: 13,
                      }}
                    >
                      Estado empleado
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: "rgba(166,166,178,0.95)",
                        color: "primary.900",
                        fontWeight: 900,
                        fontSize: 13,
                      }}
                    >
                      Acceso
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          spacing={1}
                          sx={{ py: 5 }}
                        >
                          <CircularProgress size={28} />
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "neutral.700",
                            }}
                          >
                            Cargando usuarios...
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length ? (
                    filteredUsers.map((user) => {
                      const saving = Number(savingUserId) === Number(user.userId);

                      return (
                        <TableRow
                          key={user.userId}
                          hover
                          sx={{
                            "&:nth-of-type(even)": {
                              bgcolor: "rgba(15, 23, 42, 0.025)",
                            },
                          }}
                        >
                          <TableCell>
                            <Stack spacing={0.2}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 900,
                                  color: "primary.900",
                                }}
                              >
                                {user.displayName}
                              </Typography>

                              {user.employeeId ? (
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "neutral.600",
                                  }}
                                >
                                  Empleado #{user.employeeId}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "neutral.800",
                              }}
                            >
                              {user.email}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={getRoleLabel(user.userRole)}
                              color={getRoleChipColor(user.userRole)}
                              size="small"
                              sx={{
                                fontWeight: 900,
                                minWidth: 98,
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            {user.employeeStatus ? (
                              <Chip
                                label={
                                  EMPLOYEE_STATUS_LABELS[user.employeeStatus] ??
                                  user.employeeStatus
                                }
                                size="small"
                                color={
                                  user.employeeStatus === "ACTIVE"
                                    ? "success"
                                    : "default"
                                }
                                variant="outlined"
                                sx={{ fontWeight: 800 }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "neutral.500",
                                }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Tooltip
                                title={
                                  user.isActive
                                    ? "Usuario con acceso"
                                    : "Usuario sin acceso"
                                }
                              >
                                <Chip
                                  icon={
                                    user.isActive ? (
                                      <LockOpenIcon />
                                    ) : (
                                      <LockOutlinedIcon />
                                    )
                                  }
                                  label={user.isActive ? "Activo" : "Sin acceso"}
                                  color={user.isActive ? "success" : "error"}
                                  size="small"
                                  sx={{
                                    fontWeight: 900,
                                    minWidth: 112,
                                  }}
                                />
                              </Tooltip>

                              <Switch
                                checked={user.isActive}
                                disabled={saving}
                                onChange={(event) =>
                                  openConfirm(user, event.target.checked)
                                }
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ py: 5, textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: 900,
                              color: "primary.900",
                            }}
                          >
                            No se encontraron usuarios.
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "neutral.600",
                              mt: 0.5,
                            }}
                          >
                            Probá actualizar la lista o cambiar la búsqueda.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(pendingChange)}
        onClose={closeConfirm}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            width: "100%",
            maxWidth: 460,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 19,
            fontWeight: 900,
            color: "primary.900",
            pb: 1,
          }}
        >
          {pendingChange?.nextIsActive
            ? "Activar acceso"
            : "Desactivar acceso"}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "neutral.800",
            }}
          >
            {pendingChange?.nextIsActive
              ? "El usuario volverá a poder ingresar al sistema."
              : "El usuario no podrá iniciar sesión ni seguir usando el sistema."}
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "16px",
              bgcolor: "rgba(14, 50, 149, 0.08)",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 900,
                color: "primary.900",
              }}
            >
              {pendingChange?.user?.displayName}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "neutral.700",
                mt: 0.3,
              }}
            >
              {pendingChange?.user?.email}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeConfirm}
            color="secondary"
            variant="outlined"
            disabled={Boolean(savingUserId)}
            sx={{
              borderRadius: "999px",
              px: 2.5,
              textTransform: "none",
              fontWeight: 800,
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={confirmChange}
            color={pendingChange?.nextIsActive ? "primary" : "error"}
            variant="contained"
            disabled={Boolean(savingUserId)}
            sx={{
              borderRadius: "999px",
              px: 2.5,
              textTransform: "none",
              fontWeight: 800,
            }}
          >
            {savingUserId
              ? "Guardando..."
              : pendingChange?.nextIsActive
              ? "Activar"
              : "Desactivar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}